/* eslint-disable consistent-return */
import { createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import i18next from "i18next";

import { setIsSpinnerVisible } from "store/modules/AppConfig/index.store";

import DealerCare from "services/dealerCareApi";

import {
  DealerDriversSearchRequest,
  DealerDriversSearchResponse,
  DealerUserCreateRequest,
  DealerUserCreateResponse,
  DealerUserDeleteRequest,
  DealerUserEditRequest,
  DealerUserEditResponse,
  DealerUserRecoveryPasswordRequest,
  DealerUserRecoveryPasswordResponse,
  DealerUserSendWelcomeMailRequest,
  DealerUserSendWelcomeMailResponse,
  DealerUsersSearchRequest,
  DealerUsersSearchResponse,
  LogisticUsersSearchRequest,
  LogisticUsersSearchResponse,
} from "./types.store";
import logisticsApi from "services/logisticsApi";
import { getDealerDrivers } from "services/getDealerDrivers";

function buildDealerUsersRequestKey(params: DealerUsersSearchRequest): string {
  return [
    params.dealerId,
    params.limit,
    params.offset,
    params.profile ?? "",
    params.statusOn ?? "",
  ].join(":");
}

const inflightDealerUsersRequests = new Map<
  string,
  Promise<DealerUsersSearchResponse>
>();

async function fetchDealerUsers(
  params: DealerUsersSearchRequest,
  dispatch: (action: unknown) => unknown,
): Promise<DealerUsersSearchResponse> {
  const shouldShowSpinner = !params.suppressGlobalSpinner;

  try {
    if (shouldShowSpinner) {
      dispatch(setIsSpinnerVisible(true));
    }

    const offsetLimit = params.offset * params.limit;
    const isProfile = params.profile ? `&profile=${params.profile}` : "";
    const isStatus = params.statusOn ? `&status=${params.statusOn}` : "";

    const { data } = await DealerCare.get(
      `/v1/dealers/${params.dealerId}/users?limit=${params.limit}&offset=${offsetLimit}${isStatus}${isProfile}`,
    );

    return data;
  } finally {
    if (shouldShowSpinner) {
      dispatch(setIsSpinnerVisible(false));
    }
  }
}

export const handleGetDealerUsersRequest = createAsyncThunk<
  DealerUsersSearchResponse,
  DealerUsersSearchRequest
>("getSearchDealerUsers", async (params, { rejectWithValue, dispatch }) => {
  const requestKey = buildDealerUsersRequestKey(params);
  const inflightRequest = inflightDealerUsersRequests.get(requestKey);

  if (inflightRequest && !params.forceRefresh) {
    return inflightRequest;
  }

  const requestPromise = fetchDealerUsers(params, dispatch).finally(() => {
    if (inflightDealerUsersRequests.get(requestKey) === requestPromise) {
      inflightDealerUsersRequests.delete(requestKey);
    }
  });

  inflightDealerUsersRequests.set(requestKey, requestPromise);

  return requestPromise.catch((err) => {
    if (!params.suppressGlobalSpinner) {
      toast(i18next.t("failedLoadUsersPleaseAgain").toString(), {
        type: "error",
        autoClose: false,
      });
    }

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  });
});

export const handleGetLogisticUsersRequest = createAsyncThunk<
  LogisticUsersSearchResponse,
  LogisticUsersSearchRequest
>("getSearchDealerUsers", async (params, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    const { data } = await logisticsApi.get(
      `/v1/driver/location?dealerId=${params.dealerId}`,
    );

    return data;
  } catch (err) {
    toast(i18next.t("failedLoadUsersPleaseAgain").toString(), {
      type: "error",
      autoClose: false,
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleGetDealerDriversRequest = createAsyncThunk<
  DealerDriversSearchResponse,
  DealerDriversSearchRequest
>("getDealerDrivers", async (params, { rejectWithValue, dispatch }) => {
  const shouldShowSpinner = !params.suppressGlobalSpinner;

  try {
    if (shouldShowSpinner) {
      dispatch(setIsSpinnerVisible(true));
    }

    const data = await getDealerDrivers({
      dealerId: params.dealerId,
      limit: params.limit,
      keyset: params.keyset,
      statuses: params.statuses,
    });

    return { data };
  } catch (err) {
    if (!params.suppressGlobalSpinner) {
      toast(i18next.t("failedLoadUsersPleaseAgain").toString(), {
        type: "error",
        autoClose: false,
      });
    }

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    if (shouldShowSpinner) {
      dispatch(setIsSpinnerVisible(false));
    }
  }
});

export const sendEmailWelcomeRequest = createAsyncThunk<
  DealerUserSendWelcomeMailResponse,
  DealerUserSendWelcomeMailRequest
>("postSendWelcomeByMail", async (body, { rejectWithValue }) => {
  try {
    await DealerCare.post(`/v1/dealers/SendWelcomeByMail`, body);
  } catch (err) {
    toast(i18next.t("failedToSendMail").toString(), {
      type: "error",
      autoClose: false,
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  }
});

export const sendEmailRecoverPasswordRequest = createAsyncThunk<
  DealerUserRecoveryPasswordResponse,
  DealerUserRecoveryPasswordRequest
>("postRecoverPasswordByMail", async (body, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    await DealerCare.post(`/v1/dealers/RecoverPasswordByMail`, body);
  } catch (err) {
    toast(i18next.t("failedToSendMail").toString(), {
      type: "error",
      autoClose: false,
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleCreateDealerUserRequest = createAsyncThunk<
  DealerUserCreateResponse,
  DealerUserCreateRequest
>("postCreateDealerUser", async (body, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    await DealerCare.post<{ data: { id: string } }>(
      `/v1/dealers/${body.dealerId}/users`,
      body,
    );

    await dispatch(
      handleGetDealerUsersRequest({
        dealerId: body.dealerId,
        limit: 20,
        offset: 0,
      }),
    );
  } catch (err) {
    toast(i18next.t("failedCreateUserPleaseAgain").toString(), {
      type: "error",
      autoClose: false,
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleEditDealerUserRequest = createAsyncThunk<
  DealerUserEditResponse,
  DealerUserEditRequest
>("putEditDealerUser", async (body, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    await DealerCare.put(
      `/v1/dealers/${body.dealerId}/users/${body.userId}`,
      body,
    );

    await dispatch(
      handleGetDealerUsersRequest({
        dealerId: body.dealerId,
        limit: 20,
        offset: 0,
      }),
    );
  } catch (err) {
    toast(i18next.t("failedEditUserPleaseAgain").toString(), {
      type: "error",
      autoClose: false,
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleDeleteDealerUserRequest = createAsyncThunk<
  void,
  DealerUserDeleteRequest
>("deleteDeleteDealerUser", async (body, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    await DealerCare.delete(
      `/v1/dealers/${body.dealerId}/users/${body.userId}`,
    );

    await dispatch(
      handleGetDealerUsersRequest({
        dealerId: body.dealerId,
        limit: 20,
        offset: 0,
      }),
    );
  } catch (err: any) {
    if (body.profile === "deliveryman") {
      return rejectWithValue(err.response.data);
    }

    toast(i18next.t("failedDeleteUserPleaseAgain").toString(), {
      type: "error",
      autoClose: false,
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});
